import { useEffect, useRef } from "react";
import * as Sentry from "@sentry/react";
import { toast } from "react-toastify";

import { useModal } from "context/ModalContext";

const PROMPT_COOLDOWN_MS = 2 * 60 * 1000;
const IGNORED_ERROR_PATTERNS = ["ResizeObserver", "Script error"];

export default function ErrorReportListener() {
  const { modals, openModal } = useModal();
  const lastPromptAtRef = useRef(0);
  const isReportOpenRef = useRef(false);
  const openModalRef = useRef(openModal);

  const isReportOpen = modals.some((modal) => modal.id === "crash-report" && modal.isOpen);

  useEffect(() => {
    isReportOpenRef.current = isReportOpen;
    openModalRef.current = openModal;
  }, [isReportOpen, openModal]);

  useEffect(() => {
    const promptToReport = (errorMessage: string) => {
      if (IGNORED_ERROR_PATTERNS.some((pattern) => errorMessage.includes(pattern))) {
        return;
      }
      if (isReportOpenRef.current || Date.now() - lastPromptAtRef.current < PROMPT_COOLDOWN_MS) {
        return;
      }
      lastPromptAtRef.current = Date.now();

      setTimeout(() => {
        const eventId = Sentry.lastEventId();
        toast.error(
          ({ closeToast }) => (
            <div className="flex flex-col items-start gap-2">
              <span>Something went wrong.</span>
              <button
                type="button"
                className="px-3 py-1 text-sm font-semibold rounded-md bg-white/20"
                onClick={() => {
                  closeToast();
                  openModalRef.current("crash-report", {
                    source: "error",
                    eventId,
                    errorMessage
                  });
                }}>
                Report this issue
              </button>
            </div>
          ),
          {
            position: "bottom-right",
            className: "bg-error text-white",
            autoClose: 15000
          }
        );
      }, 0);
    };

    const handleError = (event: ErrorEvent) => {
      promptToReport(event.message || "Unknown error");
    };

    const handleRejection = (event: PromiseRejectionEvent) => {
      const { reason } = event;
      promptToReport(
        reason instanceof Error ? reason.message : String(reason ?? "Unhandled rejection")
      );
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleRejection);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleRejection);
    };
  }, []);

  return null;
}
